import { useCallback, useEffect, useState } from 'react';
import { http } from '../../lib/api';
import { downloadAllCsv, MAX_EXPORT_ROWS } from '../../lib/csv';
import { AppLayout } from '../../components/layout';
import { Alert, Button, Card, Input, MetricCard, Select, StatusBadge } from '../../components/core';
import { DataTable, type Column, Pagination, SpinnerBlock } from '../../components/feedback';
import { invoiceColumn, InvoiceModal } from '../../components/invoice';
import { useUiStore } from '../../store/ui';
import { useAuthStore } from '../../store/auth';
import type { TenantPaymentRowDto, TenantPaymentsPageDto } from '@livestreamx/shared-types';
import { NAV } from './nav';

const PAGE_SIZE = 25;
const METHODS = ['CASH', 'BANK_TRANSFER', 'UPI', 'CARD', 'CHEQUE', 'OTHER'];
const STATUSES = ['PENDING', 'PAID', 'FAILED', 'REFUNDED'];

const cur = (n: number, currency = 'INR') =>
  n.toLocaleString('en-IN', { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 2 });
const num = (n: number) => n.toLocaleString('en-IN');

/** ISO → `1 Sep 2026`; returns the raw value if it doesn't parse. */
function day(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

interface Filters {
  search: string;
  status: string;
  method: string;
  from: string;
  to: string;
}

const EMPTY: Filters = { search: '', status: '', method: '', from: '', to: '' };

/** `YYYY-MM-DD` picker values widened to whole days, so `to` includes its own date. */
function toQuery(f: Filters) {
  return {
    search: f.search || undefined,
    status: f.status || undefined,
    method: f.method || undefined,
    from: f.from ? `${f.from}T00:00:00` : undefined,
    to: f.to ? `${f.to}T23:59:59` : undefined,
  };
}

const CSV_HEADER = ['Date', 'Plan', 'Cycle', 'Stream', 'Amount', 'Currency', 'Status', 'Method', 'Reference', 'Receipt', 'Provider', 'Note', 'Booked at'];
const toCsvRow = (r: TenantPaymentRowDto) => [
  r.paidAt, r.planName, r.billingCycle, r.streamName ?? 'Stream not created', r.amount, r.currency,
  r.status, r.method, r.providerRef, r.receipt, r.provider, r.note, r.createdAt,
];

const columns: Column<TenantPaymentRowDto>[] = [
  {
    key: 'date',
    header: 'Date',
    render: (r) => (
      <div>
        <div className="font-medium">{day(r.paidAt)}</div>
        {/* A back-dated hand-collected payment is written after the money moved. */}
        {r.paidAt && day(r.paidAt) !== day(r.createdAt) && (
          <div className="text-muted text-xs">booked {day(r.createdAt)}</div>
        )}
      </div>
    ),
  },
  {
    key: 'plan',
    header: 'Plan',
    render: (r) => (
      <span className="text-muted text-sm">
        {r.planName ?? '—'}{r.billingCycle ? ` · ${r.billingCycle.toLowerCase()}` : ''}
      </span>
    ),
  },
  {
    key: 'stream',
    header: 'Stream',
    render: (r) => (
      <span className="text-muted text-sm">{r.streamName ?? 'Stream not created'}</span>
    ),
  },
  { key: 'amount', header: 'Amount', render: (r) => <span className="font-medium">{cur(r.amount, r.currency)}</span> },
  { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  { key: 'method', header: 'Method', render: (r) => <span className="text-muted text-sm">{r.method?.replace('_', ' ') ?? '—'}</span> },
  {
    key: 'ref',
    header: 'Reference',
    render: (r) => (
      <span className="text-muted text-sm">
        {r.providerRef ?? '—'}
        {/* The receipt usually repeats the gateway id; only show it when it differs. */}
        {r.receipt && r.receipt !== r.providerRef ? ` · ${r.receipt}` : ''}
      </span>
    ),
  },
  { key: 'note', header: 'Note', render: (r) => <span className="text-muted text-sm">{r.note ?? '—'}</span> },
];

export function TransactionsPage() {
  const [data, setData] = useState<TenantPaymentsPageDto | null>(null);
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [invoice, setInvoice] = useState<TenantPaymentRowDto | null>(null);
  const tenantName = useAuthStore((s) => s.tenant?.name) ?? '—';
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const toast = useUiStore((s) => s.toast);

  // Debounced, so typing in the search box doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((f) => (f.search === search ? f : { ...f, search }));
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const set = useCallback(<K extends keyof Filters>(key: K, value: Filters[K]) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(1);
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    http
      .get<TenantPaymentsPageDto>('/api/billing/payments', { page, pageSize: PAGE_SIZE, ...toQuery(filters) })
      .then(setData)
      .catch((e) => setError((e as Error).message ?? 'Failed to load transactions'))
      .finally(() => setLoading(false));
  }, [page, filters]);

  useEffect(() => {
    void load();
  }, [load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const query = toQuery(filters);
      const { written, truncated } = await downloadAllCsv<TenantPaymentRowDto>({
        filename: 'transactions.csv',
        header: CSV_HEADER,
        fetchPage: (p) => http.get<TenantPaymentsPageDto>('/api/billing/payments', { page: p, pageSize: 100, ...query }),
        toRow: toCsvRow,
      });
      toast(
        truncated ? 'warning' : 'success',
        `Exported ${num(written)} transaction${written === 1 ? '' : 's'}`,
        truncated ? `Capped at ${num(MAX_EXPORT_ROWS)} rows — narrow the filters for a complete file.` : undefined,
      );
    } catch (e) {
      toast('danger', 'Export failed', (e as Error).message);
    }
    setExporting(false);
  };

  const filtered = !!(filters.search || filters.status || filters.method || filters.from || filters.to);
  const reset = () => {
    setSearch('');
    setFilters(EMPTY);
    setPage(1);
  };

  return (
    <AppLayout brand="LiveStreamX" navGroups={NAV}>
      <div className="flex justify-between items-center mb-2">
        <div>
          <h1 className="page-title">Transaction History</h1>
          <p className="page-subtitle">Every payment recorded for this workspace — gateway and hand-collected</p>
        </div>
        <Button variant="secondary" onClick={exportCsv} disabled={exporting || !data?.total}>
          {exporting ? 'Exporting…' : '⭳ Download CSV'}
        </Button>
      </div>
      {error && <Alert kind="danger">{error}</Alert>}

      {data && (
        <div className="grid-stats">
          <MetricCard label="Transactions" value={num(data.summary.count)} hint={filtered ? 'Matching filters' : 'All time'} />
          <MetricCard label="Collected" value={cur(data.summary.paidAmount)} tone="success" hint="Paid" />
          <MetricCard label="Pending" value={cur(data.summary.pendingAmount)} tone="warning" hint="Not yet settled" />
          <MetricCard label="Refunded" value={cur(data.summary.refundedAmount)} tone="info" />
          <MetricCard label="Failed" value={num(data.summary.failedCount)} tone="danger" />
        </div>
      )}

      <div className="flex flex-wrap items-end gap-2 mt-3 mb-2">
        <div style={{ flex: 1, minWidth: 240 }}>
          <Input placeholder="Search order id, UTR, receipt…" value={search} onChange={setSearch} />
        </div>
        <div style={{ minWidth: 150 }}>
          <Select
            value={filters.status}
            onChange={(v) => set('status', v)}
            options={[{ value: '', label: 'All statuses' }, ...STATUSES.map((s) => ({ value: s, label: s }))]}
          />
        </div>
        <div style={{ minWidth: 170 }}>
          <Select
            value={filters.method}
            onChange={(v) => set('method', v)}
            options={[{ value: '', label: 'All methods' }, ...METHODS.map((m) => ({ value: m, label: m.replace('_', ' ') }))]}
          />
        </div>
        <div style={{ minWidth: 150 }}>
          <Input label="From" type="date" value={filters.from} onChange={(v) => set('from', v)} />
        </div>
        <div style={{ minWidth: 150 }}>
          <Input label="To" type="date" value={filters.to} onChange={(v) => set('to', v)} />
        </div>
        {filtered ? <Button variant="secondary" onClick={reset}>Clear</Button> : null}
      </div>

      {loading && !data ? <SpinnerBlock /> : (
        <Card>
          <DataTable columns={[...columns, invoiceColumn<TenantPaymentRowDto>(setInvoice)]} rows={data?.items ?? []} />
          {!data?.items.length && (
            <p className="text-muted text-sm" style={{ margin: 0 }}>
              {filtered
                ? 'No transactions match these filters.'
                : 'No payments recorded yet — renew a stream or subscribe to a plan and the receipt will appear here.'}
            </p>
          )}
          <Pagination page={page} totalPages={data?.totalPages ?? 1} onPage={setPage} />
        </Card>
      )}

      {invoice && (
        <InvoiceModal r={invoice} billedTo={tenantName} onClose={() => setInvoice(null)} />
      )}
    </AppLayout>
  );
}
